import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  DatePicker,
  Space,
  Row,
  Col,
  message,
  Statistic,
  Tag,
  Radio,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
  PieChartOutlined,
  TableOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

// 游戏来源数据接口
interface GameSourceData {
  id: number;
  sourceType: string; // 来源类型
  sourceTypeName: string; // 来源类型名称
  accessCount: number; // 访问次数
  percentage: number; // 占比（%）
  userCount: number; // 用户数
  betCount: number; // 投注次数
  betAmount: number; // 投注金额
  conversionRate: number; // 转化率（访问到投注的转化率）
  avgBetAmount: number; // 平均投注金额
}

type GameCategory = 'all' | 'self_live' | 'self_slot' | 'others';

const gameCategoryOptions: { value: GameCategory; label: string }[] = [
  { value: 'all', label: '全部游戏' },
  { value: 'self_live', label: '自营直播游戏' },
  { value: 'self_slot', label: '自营电子游戏' },
  { value: 'others', label: '其他游戏' },
];

// 直播间相关来源（仅自营直播游戏有）
const liveOnlySourceTypes = ['live_room_message', 'good_road_recommend', 'live_room_switch'];

// 游戏来源类型
const gameSourceTypes = [
  { value: 'game_list', name: '游戏列表' },
  { value: 'hot_recommend', name: '热门推荐' },
  { value: 'jackpot_broadcast', name: '大奖播报' },
  { value: 'live_room_message', name: '直播间消息' },
  { value: 'good_road_recommend', name: '好路推荐' },
  { value: 'live_room_switch', name: '直播间切换' },
];

// 生成随机数据
const randomInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
const randomFloat = (min: number, max: number) => Math.random() * (max - min) + min;

// 生成游戏来源数据
const generateGameSourceData = (): GameSourceData[] => {
  const data: GameSourceData[] = [];
  let totalAccess = 0;

  // 先生成数据
  gameSourceTypes.forEach((type, index) => {
    const accessCount = randomInt(1000, 50000);
    totalAccess += accessCount;
    
    data.push({
      id: index + 1,
      sourceType: type.value,
      sourceTypeName: type.name,
      accessCount,
      percentage: 0, // 稍后计算
      userCount: Math.floor(accessCount * randomFloat(0.2, 0.8)),
      betCount: Math.floor(accessCount * randomFloat(0.1, 0.6)),
      betAmount: randomFloat(100000, 50000000),
      conversionRate: randomFloat(5, 50),
      avgBetAmount: randomFloat(100, 5000),
    });
  });

  // 计算占比
  data.forEach(item => {
    item.percentage = (item.accessCount / totalAccess) * 100;
  });

  return data.sort((a, b) => b.accessCount - a.accessCount);
};

// 格式化数字
const formatNumber = (num: number) => {
  return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// 饼图颜色
const pieColors = [
  '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2',
];

const GameSourceAnalysis: React.FC = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(7, 'day'),
    dayjs(),
  ]);
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<GameSourceData[]>(generateGameSourceData());
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');
  const [gameCategory, setGameCategory] = useState<GameCategory>('all');

  // 根据游戏类型过滤来源（自营电子/其他游戏不展示直播相关来源）
  const filteredDataSource = dataSource.filter((item) => {
    if (gameCategory === 'self_live' || gameCategory === 'all') {
      return true;
    }
    // 自营电子、其他游戏：去掉直播间切换、直播间消息、好路推荐
    if (gameCategory === 'self_slot' || gameCategory === 'others') {
      return !liveOnlySourceTypes.includes(item.sourceType);
    }
    return true;
  });

  // 计算总计（基于筛选后的数据）
  const totalData = filteredDataSource.reduce((acc, item) => ({
    accessCount: acc.accessCount + item.accessCount,
    userCount: acc.userCount + item.userCount,
    betCount: acc.betCount + item.betCount,
    betAmount: acc.betAmount + item.betAmount,
  }), {
    accessCount: 0,
    userCount: 0,
    betCount: 0,
    betAmount: 0,
  });

  // 当前筛选条件下的访问总次数，用于计算占比
  const totalAccessForPercentage = filteredDataSource.reduce(
    (sum, item) => sum + item.accessCount,
    0,
  );

  const getPercentage = (item: GameSourceData) => {
    if (!totalAccessForPercentage) return 0;
    return (item.accessCount / totalAccessForPercentage) * 100;
  };

  const avgConversionRate = filteredDataSource.length > 0
    ? filteredDataSource.reduce((sum, item) => sum + item.conversionRate, 0) / filteredDataSource.length
    : 0;

  // 处理搜索
  const handleSearch = () => {
    setLoading(true);
    setTimeout(() => {
      setDataSource(generateGameSourceData());
      setLoading(false);
      message.success('查询成功');
    }, 500);
  };

  // 处理重置
  const handleReset = () => {
    setDateRange([dayjs().subtract(7, 'day'), dayjs()]);
    setDataSource(generateGameSourceData());
    setGameCategory('all');
    message.info('已重置');
  };

  // 处理导出Excel
  const handleExportExcel = () => {
    message.success('Excel导出功能开发中...');
  };

  // 表格列定义
  const columns = [
    {
      title: '来源类型',
      dataIndex: 'sourceTypeName',
      key: 'sourceTypeName',
      width: 150,
      fixed: 'left' as const,
    },
    {
      title: '访问次数',
      dataIndex: 'accessCount',
      key: 'accessCount',
      width: 120,
      align: 'right' as const,
      sorter: (a: GameSourceData, b: GameSourceData) => a.accessCount - b.accessCount,
      render: (num: number) => num.toLocaleString(),
    },
    {
      title: '占比',
      dataIndex: 'percentage',
      key: 'percentage',
      width: 150,
      align: 'right' as const,
      sorter: (a: GameSourceData, b: GameSourceData) => getPercentage(a) - getPercentage(b),
      render: (_: number, record: GameSourceData, index: number) => {
        const percentage = getPercentage(record);
        return (
          <div>
            <div style={{ marginBottom: 4 }}>
              <Tag color={pieColors[index % pieColors.length]}>
                {percentage.toFixed(2)}%
              </Tag>
            </div>
            <div style={{ width: '100%', height: 8, backgroundColor: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
              <div
                style={{
                  width: `${percentage}%`,
                  height: '100%',
                  backgroundColor: pieColors[index % pieColors.length],
                  transition: 'width 0.3s',
                }}
              />
            </div>
          </div>
        );
      },
    },
    {
      title: '用户数',
      dataIndex: 'userCount',
      key: 'userCount',
      width: 120,
      align: 'right' as const,
      sorter: (a: GameSourceData, b: GameSourceData) => a.userCount - b.userCount,
      render: (num: number) => num.toLocaleString(),
    },
    {
      title: '投注次数',
      dataIndex: 'betCount',
      key: 'betCount',
      width: 120,
      align: 'right' as const,
      sorter: (a: GameSourceData, b: GameSourceData) => a.betCount - b.betCount,
      render: (num: number) => num.toLocaleString(),
    },
    {
      title: '投注金额',
      dataIndex: 'betAmount',
      key: 'betAmount',
      width: 150,
      align: 'right' as const,
      sorter: (a: GameSourceData, b: GameSourceData) => a.betAmount - b.betAmount,
      render: (num: number) => formatNumber(num),
    },
    {
      title: '转化率',
      dataIndex: 'conversionRate',
      key: 'conversionRate',
      width: 120,
      align: 'right' as const,
      sorter: (a: GameSourceData, b: GameSourceData) => a.conversionRate - b.conversionRate,
      render: (rate: number) => (
        <Tag color={rate >= 30 ? 'success' : rate >= 15 ? 'warning' : 'default'}>
          {rate.toFixed(2)}%
        </Tag>
      ),
    },
    {
      title: '平均投注金额',
      dataIndex: 'avgBetAmount',
      key: 'avgBetAmount',
      width: 150,
      align: 'right' as const,
      sorter: (a: GameSourceData, b: GameSourceData) => a.avgBetAmount - b.avgBetAmount,
      render: (num: number) => formatNumber(num),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>
            游戏来源分析报表 {dateRange[0].format('YYYY-MM-DD')} - {dateRange[1].format('YYYY-MM-DD')}
          </h2>
        </div>

        {/* 查询条件 */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Space>
              <span>时间范围：</span>
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setDateRange([dates[0], dates[1]]);
                  }
                }}
                format="YYYY-MM-DD"
              />
            </Space>
          </Col>
          <Col span={16}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
              >
                查询
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleExportExcel}>
                导出Excel
              </Button>
              <Radio.Group
                value={gameCategory}
                onChange={(e) => setGameCategory(e.target.value)}
                style={{ marginLeft: 16 }}
              >
                {gameCategoryOptions.map((opt) => (
                  <Radio.Button key={opt.value} value={opt.value}>
                    {opt.label}
                  </Radio.Button>
                ))}
              </Radio.Group>
              <Radio.Group
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
                style={{ marginLeft: 16 }}
              >
                <Radio.Button value="chart">
                  <PieChartOutlined /> 饼图
                </Radio.Button>
                <Radio.Button value="table">
                  <TableOutlined /> 表格
                </Radio.Button>
              </Radio.Group>
            </Space>
          </Col>
        </Row>

        {/* 统计卡片 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="总访问次数"
                value={totalData.accessCount}
                valueStyle={{ color: '#1890ff' }}
                suffix="次"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="总用户数"
                value={totalData.userCount}
                valueStyle={{ color: '#52c41a' }}
                suffix="人"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="总投注次数"
                value={totalData.betCount}
                valueStyle={{ color: '#faad14' }}
                suffix="次"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="平均转化率"
                value={avgConversionRate}
                precision={2}
                valueStyle={{ color: '#f5222d' }}
                suffix="%"
              />
            </Card>
          </Col>
        </Row>

        {/* 饼图或表格展示 */}
        {viewMode === 'chart' ? (
          <Card
            title={
              <Space>
                <PieChartOutlined />
                <span>
                  游戏来源访问占比分布
                  {gameCategory !== 'all' && (
                    <>
                      （
                      {gameCategoryOptions.find((opt) => opt.value === gameCategory)?.label}
                      ）
                    </>
                  )}
                </span>
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ position: 'relative', width: 300, height: 300 }}>
                    {/* 使用CSS创建简单的饼图 */}
                    <svg width="300" height="300" viewBox="0 0 200 200" style={{ transform: 'rotate(-90deg)' }}>
                      {filteredDataSource.map((item, index) => {
                        const startAngle = filteredDataSource
                          .slice(0, index)
                          .reduce((sum, d) => sum + getPercentage(d) * 3.6, 0);
                        const angle = getPercentage(item) * 3.6;
                        const largeArcFlag = angle > 180 ? 1 : 0;
                        const x1 = 100 + 100 * Math.cos((startAngle * Math.PI) / 180);
                        const y1 = 100 + 100 * Math.sin((startAngle * Math.PI) / 180);
                        const x2 = 100 + 100 * Math.cos(((startAngle + angle) * Math.PI) / 180);
                        const y2 = 100 + 100 * Math.sin(((startAngle + angle) * Math.PI) / 180);
                        
                        return (
                          <path
                            key={item.id}
                            d={`M 100 100 L ${x1} ${y1} A 100 100 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                            fill={pieColors[index % pieColors.length]}
                            stroke="#fff"
                            strokeWidth="2"
                          />
                        );
                      })}
                    </svg>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ padding: '20px 0' }}>
                  {filteredDataSource.map((item, index) => (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: 16,
                        padding: '8px 0',
                      }}
                    >
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          backgroundColor: pieColors[index % pieColors.length],
                          marginRight: 12,
                          borderRadius: 2,
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 'bold' }}>{item.sourceTypeName}</span>
                          <span style={{ color: '#666', marginLeft: 16 }}>
                            {getPercentage(item).toFixed(2)}% ({item.accessCount.toLocaleString()}次)
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                          用户数：{item.userCount.toLocaleString()} | 投注次数：{item.betCount.toLocaleString()} | 转化率：{item.conversionRate.toFixed(2)}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </Card>
        ) : null}

        {/* 数据表格 */}
        <Table
          rowKey="id"
          dataSource={filteredDataSource}
          columns={columns}
          loading={loading}
          pagination={{
            total: dataSource.length,
            pageSize: 20,
            showTotal: (total) => `共 ${total} 条`,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          scroll={{ x: 1200 }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 'bold' }}>
                <Table.Summary.Cell index={0}>
                  总计
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  {totalData.accessCount.toLocaleString()}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  100.00%
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  {totalData.userCount.toLocaleString()}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  {totalData.betCount.toLocaleString()}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  {formatNumber(totalData.betAmount)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right">
                  {avgConversionRate.toFixed(2)}%
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="right">
                  {formatNumber(totalData.betAmount / (totalData.betCount || 1))}
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Card>
    </div>
  );
};

export default GameSourceAnalysis;

